import {AgEltBoxSelection, IEltBoxSelection} from "/@back@/edit/wed/features/boxSel.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {JSrcFields, JSubItem, SRC} from "/@lib@/wsp/src.js";
import {ITEM} from "/@lib@/wsp/item.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {AccelKeyMgr, Action, ActionWrapperFromSvc} from "/@lib@/commons/actions.js";
import {ICustomClipboard, IWedlet, IWedletActionCtx} from "/@back@/edit/wed/wedlets/wedlet.js";
import {AgEltBoxFloating} from "/@back@/edit/wed/features/boxTooltip.js";
import {TemplateResult} from "/@res@/litHtml/lib/template-result.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {renderAppend, xhtml} from "/@lib@/commons/utils/htmlLit.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {AppHeader, OAppHeaderInit} from "/@back@/core/widgets/appHeader.js";
import {SrcGrid} from "/@back@/wsp/widgets/srcGrid.js";
import {FocusItemSelRef} from "/@back@/wsp/actions/srcActions.js";
import {BoxId} from "/@back@/edit/wed/wedlets/box/boxId.js";
import {Label} from "/@back@/commons/basis.js";

/** Configuration d'une recherche de liens ascendants au subItem */
export interface JBoxSubItemIdRefsConf {

	label: string

	description?: string

	emptyGridMsg?: string

	fetchSrcList: (this: HTMLElement, ctx: IRegPointer<IItemUiEnv>, id: string) => Promise<JSrcFields[]>

	isVisible?: (this: HTMLElement, ctx: IRegPointer<IItemUiEnv>, id: string) => boolean
}

/**
 * Ancre pour un id des subItems.
 * <box-subitemid refsList?="box.id.refs.confs"/>
 */
export class BoxSubItemId extends BoxId implements IEltBoxSelection, ICustomClipboard {

	static DEFAULT_REFS_LIST = 'box.subitemid.refs.confs';

	reg: IReg<IItemUiEnv>;

	/** Liste de JBoxSubItemIdRefsConf. */
	refsList: string = BoxSubItemId.DEFAULT_REFS_LIST;

	private currentDetailsConf?: JBoxSubItemIdRefsConf;

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		super.configWedletElt(tpl, wedlet);
		this.reg = wedlet.wedMgr.reg;
		if (tpl.hasAttribute('refsList')) this.refsList = tpl.getAttribute('refsList');
		this.reg.installSkin("box-subitemid", this.shadowRoot);
		this.shadowRoot.appendChild(<Label icon={this.reg.env.resolver.resolve(":skin:edit/wed/box/subId.svg").url}/>);
	}

	static _templateFloating(this: void, box: BoxSubItemId): TemplateResult {
		const id = box.fragmentId;
		const wedMgr = box.wedlet.wedMgr;
		const reg = wedMgr.reg;

		const sd = Object.create((wedMgr.reg.env as IItemUiEnv).longDesc) as JSrcFields;
		sd.itSubItem = {id: box.fragmentId} as JSubItem;

		const confs: JBoxSubItemIdRefsConf[] = reg.getList<JBoxSubItemIdRefsConf>(box.refsList)?.filter(entry => !entry.isVisible || entry.isVisible.call(box, box, id));

		const appHeader = <AppHeader î={{
			moreActions: reg.mergeLists("actions:wed:box-subitemid", "actions:wed:box-id"),
			moreBar: {
				groupOrder: '* edit'
			},
			actionContext: {
				wedlet: box.wedlet,
				focusedElt: box,
			},
		} as OAppHeaderInit<IWedletActionCtx>}>
			<div class="currentId">{`Identifiant '${id}'`}</div>
		</AppHeader> as AppHeader<IWedletActionCtx>;

		const confTitle = <header/> as HTMLHeadElement;

		if (confs.length === 1) {
			box.currentDetailsConf = confs[0];
			confTitle.appendChild(<div>{box.currentDetailsConf.label}</div>);
		} else {
			const select = confTitle.appendChild(<select onchange={async () => {
				//grid.srcGridDatas.setDatas([]); flash désagréable
				const pos = Number.parseInt(select.value);
				box.currentDetailsConf = confs[pos];
				const items = await box.currentDetailsConf.fetchSrcList.call(box, box, id);
				grid.srcGridDatas.setDatas(items || []);
				grid.focus();
			}}/>) as HTMLSelectElement;
			confs.forEach((entry, pos) => {
				select.appendChild(<option value={pos} label={entry.label} title={entry.description}/>);
			});
		}

		const grid = new SrcGrid().initialize({
			reg: reg,
			hideHeaders: true,
			autoSelOnFocus: "first",
			itemHandlingReact: reg.env.infoBroker,
			defaultAction: FocusItemSelRef.SINGLETON,
			actions: reg.mergeLists("actions:boxId:shortDesc", "actions:wsp:shortDesc"),
			accelKeyMgr: new AccelKeyMgr().initFromMapActions(reg.mergeListsAsMap("accelkeys:boxId:shortDesc", "accelkeys:wsp:shortDesc")),
			emptyBody: () => <span>{box.currentDetailsConf?.emptyGridMsg || 'Aucun item'}</span>
		});

		// init avec la confs[0]
		box.currentDetailsConf = confs[0];
		const items = box.currentDetailsConf.fetchSrcList.call(box, box, id).then((resp: JSrcFields[]) => {
			grid.srcGridDatas.setDatas(resp || []);
			grid.focus();
		});

		return xhtml`<div id="main">
			${appHeader}
			${confTitle}
			${grid}
		</div>`;
	}

	async onClipboard(act: 'copy' | 'cut' | 'paste', ev?: Event, options?: Dict<any>): Promise<boolean> {
		if (act === 'copy' || act === 'cut') {
			//Pas de cut => simple copy
			copySubItem(this);
			return true;
		}
		//Pas de paste possible => on retourne true pour bloquer le comportement par défaut.
		return true;
	}

}

REG.reg.registerSkin("box-subitemid", 1, /* language=CSS */ `
	:host {
		border: 1px solid var(--border-color);
		border-radius: .3rem;
	}
	:host(:focus) {
		outline: var(--edit-input-focus);
	}

	:host([aria-pressed=true]) {
		background-color: var(--pressed-bgcolor) !important;
	}
`);

REG.reg.addToList("actions:wed:box-subitemid", "changeId", REG.LEVELAUTH_CORE_OVR, new ActionWrapperFromSvc()
	.setWrappedSvcCd("wedBoxIdChangeAction")
	.setIcon("/@skin@/edit/wed/box/changeSubId.svg")
);

REG.reg.addToList("actions:wed:box-subitemid", "editId", REG.LEVELAUTH_CORE_OVR, new ActionWrapperFromSvc()
	.setWrappedSvcCd("wedBoxIdEditAction")
	.setIcon("/@skin@/edit/wed/box/editSubId.svg")
);

AgEltBoxSelection(BoxSubItemId, {selMode: 'box', actionsLists: ['actions:wed:box-subitemid', 'actions:wed:box-id', 'actions:wed:box'], groupOrder: '* edit'});

AgEltBoxFloating(BoxSubItemId, {
	findFocusableOnShown: true,
	display: ['click'],
	factory: ((owner: BoxSubItemId, tooltip) => {
		const reg = REG.findReg(owner);
		let tpb: (rib: BoxSubItemId) => TemplateResult = reg.getSvc("box.subitemid.tpl.floating", BoxSubItemId._templateFloating);
		if (tpb) {
			const root = <div id="boxIdRoot"/> as HTMLElement;
			reg.installSkin("box-subitemid/floating", root.attachShadow(DOMSH.SHADOWDOM_INIT));
			if (owner.localName !== "box-subitemid") reg.installSkin(owner.localName + "/floating", root.shadowRoot);
			tooltip.addEventListener('c-show', (ev: CustomEvent) => {
				renderAppend(tpb(owner), root.shadowRoot);
			});
			return root;
		}
	})
});

window.customElements.define("box-subitemid", BoxSubItemId);

REG.reg.registerSkin("box-subitemid/floating", 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		min-width: 2rem;
		--row-bgcolor: var(--bgcolor);
		max-width: 40vw;
	}

	c-appheader {
		min-height: 2rem;
		z-index: 1;
		margin: 0 4px;
	}

	header {
		display: flex;
		min-height: 0;
		font-size: var(--label-size);
		padding: .2em;
		color: var(--fade-color);
		border-bottom: 1px solid var(--border-color);
		text-align: center;
		justify-content: center;
	}

	.currentId {
		align-self: center;
		color: var(--alt1-color);
	}

	select {
		flex: 1;
		min-width: 0;
		border: 1px solid var(--border-color);
		background-color: var(--form-bgcolor);
		color: var(--form-color);
		text-overflow: ellipsis;
		font-size: inherit;
	}

	wsp-src-grid {
		border: none;
	}

`);

/** conf : liens inverses */
REG.reg.registerSvc('box-subitemid.confs.revlinks.sub', 1, {
	label: "Références à cet identifiant",
	fetchSrcList: async (ctx, id: string) => {
		const ascs = await ITEM.fetchSrcNetJson(ctx.reg.env.wsp, this, ITEM.makeSrcRefSub(SRC.srcRef(ctx.reg.env.longDesc), id), ctx.reg.env.wsp.getShortDescDef(),
			"asc", true, null, null, 100, 1, null, null);
		return ascs?.lnks?.map(entry => entry.src);
	},
} as JBoxSubItemIdRefsConf);
REG.reg.addSvcToList(BoxSubItemId.DEFAULT_REFS_LIST, 'revlinks.sub', 1, 'box-subitemid.confs.revlinks.sub', -1);

/** Action : copie du srcNode*/
REG.reg.addToList("actions:wed:box-subitemid", "copy", 2, new Action<IWedletActionCtx>("copy")
	.setLabel("Copier")
	.setDescription("Copier le lien vers l'identifiant")
	.setGroup("clipboard")
	.setIcon("/@skin@/commons/icons/copy.svg")
	.setExecute(async (ctx: IWedletActionCtx, ev) => {
		copySubItem(ctx.focusedElt as BoxSubItemId);
	})
);

function copySubItem(subItemElt: BoxSubItemId) {
	if (!subItemElt.fragmentId) {
		POPUP.showNotifWarning("Impossible de copier un identifiant vide", subItemElt);
	} else {
		const wedMgr = subItemElt.wedlet.wedMgr;
		const sd = Object.create((wedMgr.reg.env as IItemUiEnv).longDesc) as JSrcFields;
		sd.itSubItem = {id: subItemElt.fragmentId, mo: subItemElt.getAttribute("model")} as JSubItem;
		ITEM.setShortDescTransferToClipboard({
				emitter: subItemElt,
				reg: wedMgr.reg,
				shortDescs: [sd]
			}
		);
	}
}
